import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from './config';
import { getRecaptchaToken } from './recaptcha';

/** peo-hub-go (donations, payments, etc.) */
export const goClient = axios.create({
  baseURL: config.GO_API_BASE_URL,
  responseType: 'json',
  timeout: 20000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
});

goClient.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('token');
    if (token && !cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

const onJsonSuccess = (response) => {
  console.debug('Request Successful!', response);
  return response.data;
};

const onJsonError = (error) => {
  console.error('Request Failed:', error.toJSON?.() || error);
  if (error.response?.status === 401) {
    const currentPath = window.location.pathname + window.location.search;
    const email = localStorage.getItem('email');
    localStorage.clear();
    if (email) {
      localStorage.setItem('email', email);
    }
    localStorage.setItem('returnUrl', currentPath);
    window.location.href = '/login';
  }
  return Promise.reject(error.response?.data || error.message);
};

/** JSON request to peo-hub-go */
export const goRequest = async (options) => {
  return goClient({ ...options })
    .then(onJsonSuccess)
    .catch(onJsonError);
};

/**
 * goRequest with reCAPTCHA v3 token in X-Recaptcha-Token (when site key is configured).
 * @param {string} recaptchaAction e.g. donation_from
 * @param {import('axios').AxiosRequestConfig} options
 */
export const goRequestWithRecaptcha = async (recaptchaAction, options) => {
  let token = '';
  try {
    token = await getRecaptchaToken(recaptchaAction);
  } catch (e) {
    console.error('reCAPTCHA token request failed', e);
  }
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers['X-Recaptcha-Token'] = token;
  }
  return goRequest({ ...options, headers });
};

export const useApiGet = (key, fn, options, stringParams = null) =>
  useQuery({
    queryKey: key,
    queryFn: stringParams ? () => fn(stringParams) : () => fn(),
    ...options,
  });

export const useApiSend = (fn, success, error, invalidateKey, options) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      invalidateKey &&
        invalidateKey.forEach((key) => {
          queryClient.invalidateQueries(key);
        });
      success && success(data);
    },
    onError: error,
    retry: 0,
    ...options,
  });
};
